import React, { ComponentProps } from 'react';
import { Grid, Text, Box, IllustrationMdHiwStylePreferences, IllustrationMdHiwStylistSelections, IllustrationMdFeatureFixes, IllustrationMdFreestyleBagsPair, IllustrationSmNoCommitmentScissors, IllustrationSmValuePropFreeShipping, IllustrationSmExpertPick } from '@stitch-fix/mode-react';
import { CloudinaryImageFields, CloudinaryImageWrapper } from '../CloudinaryImageWrapper';
import styles from './styles.module.scss';

// map the Illustration name to the component reference so that we can use it to
// validate the type and allow CMS users to select an illustration by name
const illustrationMap = {
  IllustrationMdHiwStylePreferences,
  IllustrationMdHiwStylistSelections,
  IllustrationMdFeatureFixes,
  IllustrationMdFreestyleBagsPair,
  IllustrationSmNoCommitmentScissors,
  IllustrationSmValuePropFreeShipping,
  IllustrationSmExpertPick
} as const;
type Block = {
  title: string;
  description: string;
  illustration: keyof typeof illustrationMap | null;
  image?: CloudinaryImageFields | null;
};
export interface TripleBlockProps {
  title?: string;
  blocks: Block[];
  blockSize: 'sm' | 'md' | 'lg';
}
type BlockDimensions = {
  width: ComponentProps<typeof Box>['width'];
};
const blockDimensions: Record<TripleBlockProps['blockSize'], BlockDimensions> = {
  sm: {
    width: {
      sm: '96px',
      lg: '96px'
    }
  },
  md: {
    width: {
      sm: '200px',
      lg: '250px'
    }
  },
  lg: {
    width: {
      sm: '100%'
    }
  }
};
type BlockTextProps = {
  title: ComponentProps<typeof Text>;
  description: ComponentProps<typeof Text>;
};
const blockTextProps: Record<TripleBlockProps['blockSize'], BlockTextProps> = {
  sm: {
    title: {
      setting: 'title-small'
    },
    description: {
      setting: 'body-medium',
      my: 0.5
    }
  },
  md: {
    title: {
      setting: 'title-medium',
      spacingBottom: true
    },
    description: {
      setting: 'body-small'
    }
  },
  lg: {
    title: {
      setting: 'title-medium',
      spacingBottom: true
    },
    description: {
      setting: 'body-small'
    }
  }
};
const renderBlockGraphic = (block: Block) => {
  const Illustration = block.illustration ? illustrationMap[block.illustration] : undefined;
  if (block.image?.src) {
    return <CloudinaryImageWrapper src={block.image.src} alt={block.image.alt} aspectRatio={block.image.aspectRatio} sources={{
      sm: 560
    }} maxContainerWidth="lg" sizes={[{
      minWidth: 0,
      fluidWidth: 100
    }, {
      minWidth: 900,
      staticWidth: 300
    }]} cloudinaryAsset={block.image.cloudinaryAsset} imageProps={{
      loading: 'lazy'
    }} />;
  }
  if (Illustration) {
    return <Illustration data-testid={block.illustration} />;
  }
  return null;
};
export const TripleBlock = ({
  title,
  blocks,
  blockSize
}: TripleBlockProps) => {
  return <Box as="section" className={styles.container}>
      {title && <Text as="h2" setting="title-large" align="center" mt={2} spacingBottom>
          {title}
        </Text>}
      <Grid columns={{
      sm: 1,
      lg: 3
    }}>
        {blocks.map((block, index) => {
        return (
          // List is static and won't change once rendered
          // eslint-disable-next-line react/no-array-index-key
          <Box key={index} className={styles.block}>
              {(block.illustration || block.image?.src) && <Box m="0 auto 1.25rem" width={blockDimensions[blockSize].width} style={{
              aspectRatio: '1 / 1'
            }}>
                  {renderBlockGraphic(block)}
                </Box>}
              <Text as="p" align="center" {...blockTextProps[blockSize].title}>
                {block.title}
              </Text>
              <Text as="p" align="center" mx={1} {...blockTextProps[blockSize].description}>
                {block.description}
              </Text>
            </Box>
        );
      })}
      </Grid>
    </Box>;
};